/**
 * 6 sons, synthetises au runtime. Aucun asset binaire, aucune dependance.
 *
 * L'AudioContext n'est cree qu'au premier geste utilisateur : les navigateurs
 * refusent de le demarrer avant, et en creer un au chargement laisse un contexte
 * suspendu qui pollue les outils de dev.
 *
 * Note : prefers-reduced-motion ne coupe PAS le son. C'est une preference de
 * mouvement, pas d'audio : seul le bouton mute agit dessus.
 */
export type SoundName = 'join' | 'submit' | 'found' | 'missed' | 'tick' | 'podium';

/** Volume general, volontairement bas : le jeu se joue souvent en groupe. */
const MASTER_GAIN = 0.12;

let context: AudioContext | null = null;
let master: GainNode | null = null;
let muted = false;

type AudioContextCtor = typeof AudioContext;

function audioContextCtor(): AudioContextCtor | null {
  const scope = window as unknown as {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  return scope.AudioContext ?? scope.webkitAudioContext ?? null;
}

/** A appeler sur le premier geste utilisateur. Idempotent. */
export function unlockAudio(): void {
  if (context !== null) {
    if (context.state === 'suspended') void context.resume();
    return;
  }
  const Ctor = audioContextCtor();
  if (Ctor === null) return;

  context = new Ctor();
  master = context.createGain();
  master.gain.value = muted ? 0 : MASTER_GAIN;
  master.connect(context.destination);
}

export function setMuted(next: boolean): void {
  muted = next;
  if (master !== null && context !== null) {
    // Rampe courte : couper net produit un clic audible.
    master.gain.setTargetAtTime(next ? 0 : MASTER_GAIN, context.currentTime, 0.02);
  }
}

interface ToneOptions {
  readonly frequency: number;
  readonly duration: number;
  readonly type?: OscillatorType;
  readonly delay?: number;
  readonly gain?: number;
  /** Glissando vers cette frequence. */
  readonly slideTo?: number;
}

function tone(options: ToneOptions): void {
  if (context === null || master === null) return;

  const start = context.currentTime + (options.delay ?? 0);
  const oscillator = context.createOscillator();
  const envelope = context.createGain();

  oscillator.type = options.type ?? 'sine';
  oscillator.frequency.setValueAtTime(options.frequency, start);
  if (options.slideTo !== undefined) {
    oscillator.frequency.exponentialRampToValueAtTime(options.slideTo, start + options.duration);
  }

  // Attaque courte puis decroissance exponentielle : evite les clics.
  const peak = options.gain ?? 1;
  envelope.gain.setValueAtTime(0.0001, start);
  envelope.gain.exponentialRampToValueAtTime(peak, start + 0.012);
  envelope.gain.exponentialRampToValueAtTime(0.0001, start + options.duration);

  oscillator.connect(envelope);
  envelope.connect(master);
  oscillator.start(start);
  oscillator.stop(start + options.duration + 0.02);
}

/** Souffle court : bruit blanc filtre, pour l'envoi du dessin. */
function whoosh(): void {
  if (context === null || master === null) return;

  const duration = 0.28;
  const frames = Math.floor(context.sampleRate * duration);
  const buffer = context.createBuffer(1, frames, context.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < frames; i += 1) {
    // Enveloppe descendante appliquee directement au bruit.
    data[i] = (Math.random() * 2 - 1) * (1 - i / frames) ** 2;
  }

  const source = context.createBufferSource();
  source.buffer = buffer;

  const filter = context.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(1400, context.currentTime);
  filter.frequency.exponentialRampToValueAtTime(320, context.currentTime + duration);
  filter.Q.value = 0.9;

  const envelope = context.createGain();
  envelope.gain.value = 0.5;

  source.connect(filter);
  filter.connect(envelope);
  envelope.connect(master);
  source.start();
}

export function playSound(name: SoundName): void {
  if (muted || context === null) return;
  if (context.state === 'suspended') void context.resume();

  switch (name) {
    // Pop bref et monte : quelqu'un arrive.
    case 'join':
      tone({ frequency: 420, slideTo: 760, duration: 0.14, type: 'triangle', gain: 0.7 });
      return;

    case 'submit':
      whoosh();
      return;

    // Carillon deux notes, quinte ascendante : la recompense.
    case 'found':
      tone({ frequency: 784, duration: 0.22, type: 'sine', gain: 0.8 });
      tone({ frequency: 1175, duration: 0.42, type: 'sine', gain: 0.6, delay: 0.11 });
      return;

    // Plop grave et descendant.
    case 'missed':
      tone({ frequency: 220, slideTo: 110, duration: 0.26, type: 'sine', gain: 0.8 });
      return;

    // Tick feutre du decompte : court, sourd, peu present.
    case 'tick':
      tone({ frequency: 660, duration: 0.07, type: 'square', gain: 0.16 });
      return;

    // Accord majeur bref pour le podium.
    case 'podium':
      tone({ frequency: 523.25, duration: 0.55, type: 'triangle', gain: 0.5 });
      tone({ frequency: 659.25, duration: 0.55, type: 'triangle', gain: 0.42, delay: 0.06 });
      tone({ frequency: 783.99, duration: 0.62, type: 'triangle', gain: 0.42, delay: 0.12 });
      return;
  }
}
